import type { OnAfterUploadFn } from '../types';
import type { Body, Meta, PluginOpts } from '@uppy/core';

import { BasePlugin } from '@uppy/core';

export type UploadLink = {
  sasLink: string;
  fileId: string;
};

export type GetUploadLink = () => Promise<UploadLink>;

export type AzureBlobUploaderPluginOptions<M extends Meta = Meta> =
  PluginOpts & {
    getUploadLink: GetUploadLink;
    onAfterUpload?: OnAfterUploadFn<M>;
  };

/**
 * Custom Uppy uploader plugin that uploads files directly to Azure Blob Storage.
 *
 * Flow per file:
 * 1. Call `getUploadLink` to get a per-file SAS URL from the backend
 * 2. PUT the file directly to Azure using the SAS URL
 *
 * Uses XMLHttpRequest for native upload progress tracking and abort support,
 * avoiding the need for the @azure/storage-blob SDK.
 */
export class AzureBlobUploaderPlugin<
  M extends Meta = Meta,
  B extends Body = Body,
> extends BasePlugin<AzureBlobUploaderPluginOptions<M>, M, B> {
  private abortControllers = new Map<string, AbortController>();

  constructor(uppy: any, opts: AzureBlobUploaderPluginOptions<M>) {
    // oxlint-disable-next-line typescript/no-unsafe-argument
    super(uppy, opts);
    this.type = 'uploader';
    this.id = opts.id ?? 'AzureBlobUploader';
  }

  private uploadToAzure(fileId: string, sasLink: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const file = this.uppy.getFile(fileId);
      // Defensive: getFile can return undefined at runtime (e.g. file removed)
      // oxlint-disable-next-line typescript/no-unnecessary-condition
      if (!file) {
        reject(new Error('File not found'));
        return;
      }

      const abortController = new AbortController();
      this.abortControllers.set(fileId, abortController);

      const xhr = new XMLHttpRequest();

      abortController.signal.addEventListener('abort', () => {
        xhr.abort();
      });

      xhr.open('PUT', sasLink);
      xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
      xhr.setRequestHeader(
        'Content-Type',
        file.type || 'application/octet-stream',
      );

      xhr.upload.addEventListener('progress', (event) => {
        if (!event.lengthComputable) {
          return;
        }

        const currentFile = this.uppy.getFile(fileId);
        // Defensive: getFile can return undefined at runtime (e.g. file removed)
        // oxlint-disable-next-line typescript/no-unnecessary-condition
        if (!currentFile) {
          return;
        }

        this.uppy.emit('upload-progress', currentFile, {
          bytesTotal: event.total,
          bytesUploaded: event.loaded,
          uploadStarted: currentFile.progress.uploadStarted ?? Date.now(),
        });
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(
            new Error(
              `Azure upload failed with status ${xhr.status}: ${xhr.statusText}`,
            ),
          );
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload to Azure'));
      });

      xhr.addEventListener('abort', () => {
        reject(
          Object.assign(new Error('Upload aborted'), { name: 'AbortError' }),
        );
      });

      xhr.send(file.data as Blob);
    });
  }

  private handleUpload = async (fileIDs: string[]) => {
    if (fileIDs.length === 0) {
      return;
    }

    const files = fileIDs
      .map((id) => this.uppy.getFile(id))
      // Defensive: getFile can return undefined at runtime
      // oxlint-disable-next-line typescript/no-unnecessary-condition
      .filter((f): f is NonNullable<typeof f> => f != null)
      .filter((f) => !f.progress.uploadComplete);

    if (files.length === 0) {
      return;
    }

    this.uppy.emit('upload-start', files);

    await Promise.allSettled(
      files.map(async (file) => {
        try {
          const uploadLink = await this.opts.getUploadLink();

          await this.uploadToAzure(file.id, uploadLink.sasLink);

          if (this.opts.onAfterUpload) {
            await this.opts.onAfterUpload(
              {
                fileId: uploadLink.fileId,
                fileName: file.name,
                fileSize: file.size,
                type: file.type ?? '',
              },
              file.meta,
            );
          }

          const successFile = this.uppy.getFile(file.id);
          // Defensive: getFile can return undefined at runtime (e.g. file removed)
          // oxlint-disable-next-line typescript/no-unnecessary-condition
          if (successFile) {
            this.uppy.emit('upload-success', successFile, {
              body: { fileId: uploadLink.fileId } as unknown as B,
              status: 200,
              uploadURL: uploadLink.sasLink.split('?')[0],
            });
          }
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            return;
          }

          const errorFile = this.uppy.getFile(file.id);
          // Defensive: getFile can return undefined at runtime (e.g. file removed)
          // oxlint-disable-next-line typescript/no-unnecessary-condition
          if (errorFile) {
            this.uppy.emit(
              'upload-error',
              errorFile,
              error instanceof Error ? error : new Error(String(error)),
            );
          }
        } finally {
          this.abortControllers.delete(file.id);
        }
      }),
    );
  };

  private onFileRemoved = (file: { id: string }) => {
    this.abortControllers.get(file.id)?.abort();
  };

  private onCancelAll = () => {
    this.abortControllers.forEach((controller) => {
      controller.abort();
    });
    this.abortControllers.clear();
  };

  install() {
    this.uppy.addUploader(this.handleUpload);
    this.uppy.on('file-removed', this.onFileRemoved);
    this.uppy.on('cancel-all', this.onCancelAll);
  }

  uninstall() {
    this.onCancelAll();
    this.uppy.removeUploader(this.handleUpload);
    this.uppy.off('file-removed', this.onFileRemoved);
    this.uppy.off('cancel-all', this.onCancelAll);
  }
}
